import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import {
    BehaviorSubject,
    combineLatest,
    // EMPTY,
    merge,
    Observable,
    of,
    Subject,
    throwError,
} from 'rxjs';
import {
    catchError,
    concatMap,
    map,
    mergeMap,
    shareReplay,
    switchMap,
    take,
    tap,
    withLatestFrom,
} from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { DialogGenericComponent } from '../../shared/dialog-generic/dialog-generic.component';
import {
    DialogConfig,
    DIALOG_TYPE,
    InputFormStructure,
    InputFormValue,
    UserState,
    USER_CAPACITY,
    TRANSER_TYPE,
    USER_ROLE,
    DialogAction,
} from '../../shared/shared.interface';
import { User } from '../models/user';
import { LocalStorageService } from './../../core/services/local-storage.service';
import * as utils from './utility-functions';
import { capitalizeFirstLetter } from './utility-functions';
import {
    // TaxInput,
    TaxRecords,
} from '../pages/notary/notary-property-info/notary-property-info.component';

@Injectable({
    providedIn: 'root',
})
export class DataService {
    bff = environment.bff;
    private user: BehaviorSubject<UserState> = new BehaviorSubject<UserState>({
        selectedRole: null,
        termsAcceptance: false,
        individual: true,
        underage: false,
    });
    private isBackOfficerSubject = new BehaviorSubject<boolean>(false);
    private requestBackOfficerApplicationsSubject = new BehaviorSubject<string>(null);
    isBackOfficer$ = this.isBackOfficerSubject.asObservable();
    user$: Observable<UserState> = this.user.asObservable();
    userInfo$ = this.httpClient
        // .get<User>(`${this.bff}/user/all-information?userTaxNumber=999999999`) // notary
        // .get<User>(`${this.bff}/user/all-information?userTaxNumber=111111111`) // buyer
        // .get<User>(`${this.bff}/user/all-information?userTaxNumber=333333333`) // buyer
        // .get<User>(`${this.bff}/user/all-information?userTaxNumber=222222222`) // seller
        .get<User>(
            `${
                this.bff
            }/user/all-information?userTaxNumber=${this.storage.getTaxNumber()}`
        ) // seller
        .pipe(
            map((res) => utils.mapUserInfo(res)),
            tap((res) => {
                this.setIndividual(res.individual);
                this.setUnderage(res.underage);
                const isBackOfficer = utils.isTaxNumberBackOfficer(res.taxNumber);
                this.isBackOfficerSubject.next(isBackOfficer);
            }),
            tap((res) => {
              this.storage.setTaxNumber(res.taxNumber);
              this.storage.setDateOfBirth(res.dateOfBirth);
            }),
            catchError(this.handleError),
            shareReplay(1)
        );


    private getCitizenApplications$ = this.getCitizeApplicationObservable().pipe(
        concatMap((v) => {
          const applicationStatus = this.requestBackOfficerApplicationsSubject.getValue() ?? "ACTIVE";
          const taxNumber = this.storage.getTaxNumber();
          const dateOfBirth = this.storage.getDateOfBirth();
          console.log(`Application status ${applicationStatus} Tax Number ${taxNumber}`)
          return this.getApplicationsForCitizen(taxNumber, dateOfBirth, applicationStatus)
            .pipe(catchError(err => of(err)))
        })
    );

    citizenApplications$ = this.getCitizenApplications$.pipe(
        catchError(this.handleError)
    );

    /**
     * Determine if current user is Back Officer
     * @returns {boolean}
     */
    public isBackOfficer() {
      return this.isBackOfficerSubject.getValue();
    }

    private getCitizeApplicationObservable(){
      // Merge citizen application request with back officer requests
      // This way when we request a different application status from the UI we re execute the api call
      return merge(this.userInfo$ , this.requestBackOfficerApplicationsSubject.asObservable());
    }

    private getApplicationsForCitizen(taxNumber:string, dateOfBirth:string,  applicationStatus: string = "NEW"): Observable<any> {
        const isBackOfficer = utils.isTaxNumberBackOfficer(taxNumber);

        let headers = new HttpHeaders().set('skipRetry', isBackOfficer.toString());
        if(!isBackOfficer)
          headers = headers.append('dob', dateOfBirth);

        const url = isBackOfficer
            ? `${this.bff}/application/admin-report/${applicationStatus}` // #FIX - This should be configurable
            : `${this.bff}/application/citizen`;
        return this.httpClient
            .get(url, {
              headers: headers
            })
            .pipe(
                tap(() =>
                    console.log(
                       isBackOfficer ? `Loaded BackOfficer applications`: `getCitizenApplications$ private observable is used`
                    )
                )
            );
    }

    private getCitizenApplicationWithId$ = (appId: string) =>
        this.httpClient
            .get(`${this.bff}/application/${appId}/citizen/app-details`)
            .pipe(catchError(this.handleError));

    citizenApplicationWithId$ = (appId: string) =>
        this.getCitizenApplicationWithId$(appId);

    private getCitizenApplicationApprovalInformation$ = (appId: string, selectedApplicationType: string) =>
    // console.log(`${this.bff}/application/${appId}/approval-information` + (selectedApplicationType == TRANSER_TYPE.INHERITANCE ?`?applTypeId=${selectedApplicationType}`:''));
        this.httpClient
            .get(
                `${this.bff}/application/${appId}/approval-information?applTypeId=${selectedApplicationType}`
            )
            .pipe(catchError(this.handleError));

    citizenApplicationApprovalInformation$ = (appId: string, selectedApplicationType: string) =>
        this.getCitizenApplicationApprovalInformation$(appId, selectedApplicationType);

    private refreshNotaryApplicationDocumentsStatus =
        new BehaviorSubject<boolean>(true);

    private refreshNotaryApplicationDocumentsStatus$ =
        this.refreshNotaryApplicationDocumentsStatus.asObservable();

    private getApplicationStatus$ = (appId: string) =>
        this.refreshNotaryApplicationDocumentsStatus$.pipe(
            switchMap(() =>
                this.httpClient
                    .get(`${this.bff}/application/${appId}/app-status`)
                    .pipe(catchError(this.handleError))
            )
        );

    citizenApplicationContractsStatus$ = (appId: string) => {
        return this.getApplicationStatus$(appId);
    };
    private refreshNotaryApplicationDocuments = new BehaviorSubject<boolean>(
        true
    );
    private refreshNotaryApplicationDocuments$ =
        this.refreshNotaryApplicationDocuments.asObservable();

    private getCitizenApplicationContracts$ = (appId: string, params) =>
        this.refreshNotaryApplicationDocuments$.pipe(
            switchMap(
                () =>
                    this.httpClient
                        .get(`${this.bff}/document/application/${appId}`, {
                            params,
                            headers: new HttpHeaders().set(
                                'skipLoader',
                                'true'
                            ),
                        })
                        .pipe(catchError(this.handleError))
                // this.refreshNotaryApplicationWithIdMock$
            )
        );

    public requestBackOfficeApplications(applicationStatus: string): void{
      this.requestBackOfficerApplicationsSubject.next(applicationStatus);
    }

    public getCitizenApplicationContracts(
        appId: string,
        citizenTaxNumber: string
    ): any {
        const params = new HttpParams().set(
            'citizenTaxNumber',
            citizenTaxNumber
        );
        return this.httpClient
            .get(`${this.bff}/document/application/${appId}`, {
                params,
                headers: new HttpHeaders().set('skipLoader', 'true'),
            })
            .pipe(catchError(this.handleError));
    }

    public confirmEdyteCallService(appId: string): Observable<any> {
        return this.httpClient
            .post(
                `${this.bff}/document/edyte-document/application/${appId}`,
                null
            )
            .pipe(catchError(this.handleError));
    }

    citizenApplicationContracts$ = (
        appId: string,
        citizenTaxNumber: string,
        atakNumber: string
    ) => {
        let params = new HttpParams();
        params = params.append('citizenTaxNumber', citizenTaxNumber);
        if (atakNumber) {
            params = params.append('atakNumber', atakNumber);
        }
        return this.getCitizenApplicationContracts$(appId, params);
    };

    private notaryApplicationsPageNumber = new BehaviorSubject<number>(0);
    private notaryApplicationsPageSize = 10;

    private getNotaryApplications$ = combineLatest([
        this.userInfo$,
        this.notaryApplicationsPageNumber.asObservable(),
    ]).pipe(
        switchMap(([, pageNumber]) =>
            this.httpClient
                .post(`${this.bff}/application/notary/paged`, {
                    pageNumber,
                    pageSize: this.notaryApplicationsPageSize,
                })
                .pipe(
                    tap(() =>
                        console.log(
                            'getNotaryApplications$ private observable is used'
                        )
                    )
                )
        ),
        shareReplay(1)
    );

    notaryApplications$ = this.getNotaryApplications$;

    notaryApplicationsPagination$ = this.getNotaryApplications$.pipe(
        map((res: any) => ({
            pageNumber: res.pageNumber,
            pageSize: res.pageSize,
            totalItems: res.totalItems,
            totalPages: res.totalPages,
            hasNext: res.hasNext,
            hasPrevious: res.hasPrevious,
        })),
        catchError(this.handleError)
    );

    notaryApplicationsList$ = this.getNotaryApplications$.pipe(
        map((res: any) => utils.mapNotaryApplications(res.data, this.router, this)),
        catchError(this.handleError)
    );

    public onNotaryApplicationsPageChange(pageNumber: number) {
        this.notaryApplicationsPageNumber.next(pageNumber);
    }

    private getBackOfficerApplications$ = this.userInfo$.pipe(
        concatMap(() =>
            this.httpClient
                //.get(`${this.bff}/application/admin-report/${appStatus}`)
                .get(`${this.bff}/application/admin-report/NEW`)
                .pipe(
                    tap(() =>
                        console.log(
                            'getBackOfficerApplications$ private observalbe is used'
                        )
                    )
                )
        )
    );

    backOfficerApplications$ = this.getBackOfficerApplications$;

    backOfficerApplicationsList$ = this.getBackOfficerApplications$.pipe(
        map((res) =>
            utils.mapBackOfficerApplications(
                res,
                this
            )
        ),
        catchError(this.handleError)
    );

    private refreshNotaryApplicationWithIdMock2 = new BehaviorSubject<any>({
        notaryVatId: '030290869',
        notaryFullname: 'ΘΕΟΔΩΡΟΣ ΧΑΛΚΙΔΗΣ',
        creationDate: '2022-06-29T15:41:00.177033',
        submissionDate: '2022-06-29T15:41:00.177014',
        status: 'COMPLETED',
        horizontalNumber: '0',
        verticalNumber: '0',
        kaekNumber: '051490529006',
        newEstateContractPath: null,
        newEstateTransferApplicationId: null,
        newEstateContractUniqueCode: null,
        addressEstate: 'ΖΑΛΟΓΓΟΥ 10',
        atakNumber: '1048482824',
        cancellationStatus: null,
        protocolNumber: '1234567',
        id: 1,
        involvedParties: [
            {
                id: 1,
                vatId: '135971286',
                role: 'BUYER',
                acceptStatus: 'ACCEPTED',
                requestedCancellation: false,
                contractPath: null,
                involvedPartyDetails: {
                    fullname: 'ΓΕΩΡΓΙΑ ΤΖΟΤΑ',
                    policeIdentity: 'ΑΟ063528',
                    fatherFirstName: 'ΠΑΝΑΓΙΩΤΗΣ',
                    dateOfBirth: '10/12/1986',
                    issuedPoliceIdentity: 'Τ.Α. ΗΡΑΚΛΕΙΟΥ',
                    occupation: null,
                    taxOffice: 'ΜΕΣΟΛΟΓΓΙΟΥ',
                    residenceAddress: 'ΝΕΟΧΩΡΙ 0',
                    martialStatus: null,
                    zipCode: '30001',
                },
            },
            {
                id: 2,
                vatId: '104179850',
                role: 'SELLER',
                acceptStatus: 'ACCEPTED',
                requestedCancellation: false,
                contractPath: null,
                involvedPartyDetails: {
                    fullname: 'ΟΛΥΜΠΙΑ ΜΑΡΚΕΛΛΟΥ',
                    policeIdentity: 'ΑΟ102174',
                    fatherFirstName: 'ΠΑΥΛΟΣ',
                    dateOfBirth: '28/12/1977',
                    issuedPoliceIdentity: 'Τ.Α. ΦΙΛΟΘΕΗΣ - ΨΥΧΙΚΟΥ ',
                    occupation: null,
                    taxOffice: 'ΨΥΧΙΚΟΥ',
                    residenceAddress: 'ΑΚΡΙΤΑ ΛΟΥΚΗ 51',
                    martialStatus: 'Άγαμος/η',
                    zipCode: '15237',
                },
            },
        ],
        isFinalContractUploaded: true,
    });

    private refreshNotaryApplicationWithId = new BehaviorSubject<boolean>(true);
    private refreshNotaryApplicationWithIdMock = new BehaviorSubject<any>([
        {
            documentId: 2,
            documentType: 'AFE_ISSUE_DOCUMENT',
            documentState: 'ERROR_RETRIEVING',
            documentExpirationInDays: null,
            notaryApproval: false,
            documentInput: {},
            documentOutput: 'Δεν μπορεί να εκδοθεί Αποδεικτικό Ενημερότητας',
            documentOutputDescList: ['test1', 'test2', 'test3'],
            notaryTaxNumber: '030290869',
            citizenTaxNumber: '104179850',
            isRefreshable: true,
        },
        {
            documentId: 3,
            documentType: 'DIAGRAM_REPORT',
            documentState: 'APPROVED',
            documentExpirationInDays: null,
            notaryApproval: true,
            documentInput: {
                diagramId: 12342,
            },
            documentOutput: null,
            notaryTaxNumber: '030290869',
            citizenTaxNumber: '104179850',
            isRefreshable: true,
        },
        {
            documentId: 4,
            documentType: 'AKD_REPORT',
            documentState: 'APPROVED',
            documentExpirationInDays: null,
            notaryApproval: true,
            documentInput: {
                protNum: 4650,
                applyYear: 2021,
                kaekNumber: '051490529006',
                propHor: 0,
                propVer: 0,
            },
            documentOutput:
                '{"uniqueReportId":"498d5c67-a461-45eb-a0a3-c7317a3a77a8"}',
            notaryTaxNumber: '030290869',
            citizenTaxNumber: '104179850',
            isRefreshable: true,
        },
        {
            documentId: 5,
            documentType: 'ENFIA_CERTIFICATE',
            documentState: 'APPROVED',
            documentExpirationInDays: null,
            notaryApproval: true,
            documentInput: {
                atakNumber: '1048482824',
            },
            documentOutput: null,
            notaryTaxNumber: '030290869',
            citizenTaxNumber: '104179850',
            isRefreshable: true,
        },
        {
            documentId: 6,
            documentType: 'CIVIL_ENGINEER_CERTIFICATE',
            documentState: 'READY',
            documentExpirationInDays: -538,
            notaryApproval: false,
            documentInput: {
                kaekNumber: '051490529006-0-0',
            },
            documentOutput: null,
            notaryTaxNumber: '030290869',
            citizenTaxNumber: '104179850',
            isRefreshable: true,
        },
        {
            documentId: 7,
            documentType: 'COMPLETION_CERTIFICATE',
            documentState: 'APPROVED',
            documentExpirationInDays: -538,
            notaryApproval: true,
            documentInput: {
                kaekNumber: '051490529006-0-0',
            },
            documentOutput: null,
            notaryTaxNumber: '030290869',
            citizenTaxNumber: '104179850',
            isRefreshable: true,
        },
        {
            documentId: 8,
            documentType: 'ENERGY_EFFICIENCY_CERTIFICATE',
            documentState: 'APPROVED',
            documentExpirationInDays: -538,
            notaryApproval: true,
            documentInput: {
                kaekNumber: '051490529006-0-0',
            },
            documentOutput: null,
            notaryTaxNumber: '030290869',
            citizenTaxNumber: '104179850',
            isRefreshable: true,
        },
        {
            documentId: 9,
            documentType: 'FLOOR_PLANS',
            documentState: 'APPROVED',
            documentExpirationInDays: -538,
            notaryApproval: true,
            documentInput: {
                kaekNumber: '051490529006-0-0',
            },
            documentOutput: null,
            notaryTaxNumber: '030290869',
            citizenTaxNumber: '104179850',
            isRefreshable: true,
        },
        {
            documentId: 10,
            documentType: 'INSURANCE_CERTIFICATE',
            documentState: 'APPROVED',
            documentExpirationInDays: null,
            notaryApproval: true,
            documentInput: {
                requestNumber: 1980547,
                requestYear: '2022',
            },
            documentOutput:
                '{\n  "amka" : "28127702240",\n  "applTitle" : "Ενημερωτικό Σημείωμα",\n  "e20CognizanceDetails" : [ {\n    "reasonDescription" : "Ασφαλιστικά ενήμερος",\n    "sectionCode" : "ΕΦΚΑ",\n    "status" : 1\n  }, {\n    "reasonDescription" : "Ασφαλιστικά ενήμερος",\n    "sectionCode" : "ΣΥΜΨΗΦΙΣΜΟΙ",\n    "status" : 1\n  }, {\n    "reason" : "1",\n    "reasonDescription" : "Ύπαρξη οφειλών",\n    "sectionCode" : "ΚΕΑΟ",\n    "status" : 2\n  }, {\n    "reasonDescription" : "Ασφαλιστικά ενήμερος",\n    "sectionCode" : "π.ΤΣΜΕΔΕ",\n    "status" : 1\n  } ],\n  "employerDebtFlg" : false,\n  "employerFullName" : "ΜΑΡΚΕΛΛΟΥ ΟΛΥΜΠΙΑ",\n  "employerId" : "0000000000",\n  "info1" : "* Δεν οφείλει ληξιπρόθεσμες εισφορές διότι δεν απασχολεί ούτε απασχόλησε προσωπικό.",\n  "info2" : "* Ασφαλιστικά ενήμερος ως ελεύθερος επαγγελματίας, αυτοαπασχολούμενος ή αγρότης.",\n  "kad" : "71120000 ΔΡΑΣΤΗΡΙΟΤΗΤΕΣ ΜΗΧΑΝΙΚΩΝ ΚΑΙ ΣΥΝΑΦΕΙΣ ΔΡΑΣΤΗΡΙΟΤΗΤΕΣ ΠΑΡΟΧΗΣ ΤΕΧΝΙΚΩΝ ΣΥΜΒΟΥΛΩΝ",\n  "ownsNonSalarianFlg" : true,\n  "taxiationNo" : "104179850",\n  "webCertWitDebitDetails" : {\n    "debitLstAnal" : [ ],\n    "subComments" : "Με βάση τα τελευταία επεξεργασμένα στοιχεία δεν είναι δυνατή η έκδοση από το σύστημα Αποδεικτικού Ασφαλιστικής Ενημερότητας, σύμφωνα με τις διατάξεις του ν. 4611/19, της αριθ. 15435/913/16.4.2020 Υπουργικής Απόφασης του Υπουργού Εργασίας & Κοινωνικών Υποθέσεων και της αριθ. Οικ. 17535/Δ1.6002/6-5-2020 κοινής Υπουργικής Απόφασης των Υπουργών Εργασίας & Κοινωνικών Υποθέσεων και Επικρατείας, από τις Ηλεκτρονικές Υπηρεσίες."\n  },\n  "wittingStatus" : "1"\n}',
            notaryTaxNumber: '030290869',
            citizenTaxNumber: '104179850',
            isRefreshable: true,
        },
        {
            documentId: 11,
            documentType: 'CONTRACT',
            documentState: 'APPROVED',
            documentExpirationInDays: null,
            notaryApproval: true,
            documentInput: {},
            documentOutput: null,
            notaryTaxNumber: '030290869',
            citizenTaxNumber: '104179850',
            isRefreshable: false,
        },
        {
            documentId: 12,
            documentType: 'MUNICIPALITY_CERTIFICATE',
            documentState: 'APPROVED',
            documentExpirationInDays: null,
            notaryApproval: true,
            documentInput: {},
            documentOutput: null,
            notaryTaxNumber: '030290869',
            citizenTaxNumber: '104179850',
            isRefreshable: false,
        },
    ]);
    private refreshNotaryApplicationWithId$ =
        this.refreshNotaryApplicationWithId.asObservable();

    private refreshNotaryApplicationWithIdMock$ =
        this.refreshNotaryApplicationWithIdMock.asObservable();

    private refreshNotaryApplicationWithIdMock2$ =
        this.refreshNotaryApplicationWithIdMock2.asObservable();

    private getNotaryApplicationWithId$ = (appId: number) =>
        this.refreshNotaryApplicationWithId$.pipe(
            switchMap(
                () =>
                    this.httpClient.get(
                        `${this.bff}/application/${appId}/notary/app-details`
                    )
                //this.refreshNotaryApplicationWithIdMock2$
            )
        );

    notaryApplicationWithId$ = (appId) =>
        this.getNotaryApplicationWithId$(appId).pipe(
            tap(() => console.log('notaryApplicationWithId$ is used'))
        );

    getKaekDetails = (appId, kaek) => {
        return this.httpClient.get(
          `${this.bff}/application/${appId}/notary/kaek-information?kaekNumber=${kaek}`,
        );
    }

    getProperties(appId, vatId = ''): Observable<any> {
        //return this.ownedPropertiesMock$;

        let params = new HttpParams();
        params = params.append('appId', appId);
        if(vatId != ''){
            params = params.append('vatId', vatId);
        }
        params = params.append('applTypeId', this.selectedApplicationType);

        return this.httpClient.get(
            `${this.bff}/application/citizen/owned-properties`,
            { params }
        );
    }

    refreshDocument(appId, docId, taxNumber, id): Observable<any> {
        const headers = new HttpHeaders({
            reqId: 'refresh',
            customId: String(id),
        });
        const endpoint = `${this.bff}/document/${docId}/notary/refresh/application/${appId}`;
        return this.httpClient.put(endpoint, {}, { headers });
    }

    approveDocument(appId, docId, taxNumber): Observable<any> {
        const headers = new HttpHeaders({
            reqId: 'approve',
        });
        const endpoint = `${this.bff}/document/${docId}/notary/approve/application/${appId}`;
        return this.httpClient.put(endpoint, {}, { headers });
    }

    updateKAEK(appId, updateRequest): Observable<any> {
        const endpoint = `${this.bff}/application/${appId}/notary/update`;
        return this.httpClient.put(endpoint, updateRequest);
    }

    addNotaryProtocol(appId, updateRequest): Observable<any> {
        const endpoint = `${this.bff}/application/${appId}/notary/protocol`;
        return this.httpClient.post(endpoint, updateRequest);
    }

    generateAppCode(appId): Observable<any> {
        const endpoint = `${this.bff}/application/${appId}/notary/generate-code`;
        return this.httpClient.get(endpoint, {});
    }

    uploadCadastreDocuments(appId, docId, docType, cadOffice, file: File , replacedDocId : string = null) {
        let params = new HttpParams();
        if (docType != null) {
            params = params.set('documentTypeId', docType);
        }
        if (cadOffice != null) {
            params = params.set('cadOfficeId', cadOffice);
        }

        const endpoint = `${this.bff}/application/${appId}/document/${docId}?`;
        if(replacedDocId != null){
          params = params.set('replacedDocId', replacedDocId);
        }
        const formData: FormData = new FormData();
        formData.append('file', file, file.name);

        return this.httpClient
            .post(endpoint, formData, { params })
            .pipe(catchError(this.handleError));
    }
    uploadFileNew(appId, docId, fileToUpload: File): Observable<any> {
        const endpoint = `${this.bff}/document/${docId}/application/${appId}`;
        const formData: FormData = new FormData();
        formData.append('file', fileToUpload, fileToUpload.name);
        return this.httpClient.post(endpoint, formData);
    }
    uploadZipFile(appId, fileToUpload: File, documentType, citizenTaxNumber, atakNumber): Observable<any> {
        let params = new HttpParams();
        params = params.append('documentType', documentType);
        params = params.append('citizenTaxNumber', citizenTaxNumber);
        params = params.append('atakNumber', atakNumber);
        // const endpoint = `http://localhost:9000/notary/application/${appId}/zip`;
        const endpoint = `${this.bff}/document/notary/application/${appId}/zip`;
        const formData: FormData = new FormData();
        formData.append('file', fileToUpload, fileToUpload.name);
        return this.httpClient.post(endpoint, formData, { params });
    }

    uploadMiscFile(
        appId,
        atakNumber,
        fileToUpload: File,
        citizenTaxNumber: string,
        documentParams: any
    ): Observable<any> {
        const endpoint = `${this.bff}/document/application/${appId}/misc`;
        const formData: FormData = new FormData();
        const headers = new HttpHeaders();
        formData.append('file', fileToUpload, fileToUpload.name);
        return this.httpClient.post(endpoint, formData, {
            params: { atakNumber, citizenTaxNumber, ...documentParams },
            headers,
        });
    }

    deleteZip(appId, docType, citizenTaxNumber, atakNumber): Observable<any> {
        let params = new HttpParams();
        params = params.append('documentType', docType);
        params = params.append('citizenTaxNumber', citizenTaxNumber);
        params = params.append('atakNumber', atakNumber);
        // const endpoint = `http://localhost:9000/notary/application/${appId}/zip`;
        const endpoint = `${this.bff}/document/notary/application/${appId}/zip`;
        return this.httpClient.delete(endpoint, { params });
    }

    deleteMiscFile(appId, documentId): Observable<any> {
        return this.httpClient
            .delete(
                `${this.bff}/document/application/${appId}/misc/${documentId}`
            )
            .pipe(catchError(this.handleError));
    }

    getDocument(appId, docId, docType?) {
        this.getSpecificDocument(appId, docId).subscribe(
            // TODO fix
            (response) => {
                const filename = response.headers
                    .get('content-disposition')
                    .split('[')[1]
                    .split(']')[0];
                if (
                    /Android|webOS|iPhone|iPad|Mac|Macintosh|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
                        navigator.userAgent
                    )
                ) {
                    this.downloadFile(
                        response.body,
                        response.body.type,
                        filename
                    );
                } else {
                    if (!filename.includes('.pdf')) {
                        this.downloadFile(
                            response.body,
                            response.body.type,
                            filename
                        );
                    } else {
                        if (docType) {
                            if (docType === 'TEEBUILD_DOCUMENTS') {
                                this.downloadFile(
                                    response.body,
                                    response.body.type,
                                    filename
                                );
                            }
                            if (docType === 'CADASTRAL_DOCUMENTS') {
                                this.downloadFile(
                                    response.body,
                                    response.body.type,
                                    filename
                                );
                            }
                            if (
                                docType === 'ENERGY_EFFICIENCY_CERTIFICATE' ||
                                docType === 'COMPLETION_CERTIFICATE' ||
                                docType === 'FLOOR_PLANS' ||
                                docType === 'GEMI_CERTIFICATE_OF_REPRESENTATION' ||
                                docType === 'GENERAL_GEMI_CERTIFICATE' ||
                                docType === 'DEATH_CERTIFICATE'
                            ) {
                                if (filename.includes('.zip')) {
                                    this.downloadFile(
                                        response.body,
                                        response.body.type,
                                        filename
                                    );
                                } else {
                                    if (!filename.includes('.pdf')) {
                                        this.downloadFile(
                                            response.body,
                                            response.body.type,
                                            filename
                                        );
                                    } else {
                                        this.showFileInNewTab(response.body);
                                    }
                                }
                            }
                        } else {
                            if (!filename.includes('.pdf')) {
                                this.downloadFile(
                                    response.body,
                                    response.body.type,
                                    filename
                                );
                            } else {
                                this.showFileInNewTab(response.body);
                            }
                        }
                    }
                }
            },
            (error) => {
                // console.log('error');
            }
        );
    }

    getDocumentOutput(docId): Observable<any> {
        const endpoint = `${this.bff}/document/${docId}/output`;
        return this.httpClient.get(endpoint, {
            responseType: 'text' as 'json',
            observe: 'body',
        });
    }

    getSpecificDocument(appId, docId): Observable<any> {
        const endpoint = `${this.bff}/document/${docId}/application/${appId}`;
        return this.httpClient.get(endpoint, {
            responseType: 'blob' as 'json',
            observe: 'response',
        });
    }

    cancelApplicationByCitizen(appId): Observable<any> {
        const endpoint = `${this.bff}/application/${appId}/citizen/cancel`;
        return this.httpClient.delete(endpoint);
    }

    submitApplication(appId, acceptManualSubmit): Observable<any> {
        const endpoint =
            `${this.bff}/application/${appId}/notary/submit?acceptManualSubmit=` +
            acceptManualSubmit;
        return this.httpClient.put(endpoint, {});
    }

    getProofOfTaxPaymentInputs(
        taxNumber,
        atakNumber,
        ownerVatId
    ): Observable<TaxRecords> {
        const endpoint =
            `${this.bff}/application/notary/transfer-tax-data?buyerTaxNumber=` +
            taxNumber +
            `&sellerTaxNumber=` +
            ownerVatId +
            `&atakNumber=` +
            atakNumber;
        return this.httpClient.get<TaxRecords>(endpoint, this.httpOptions);
    }

    cancelApplicationByNotary(appId): Observable<any> {
        const endpoint = `${this.bff}/application/${appId}/notary/cancel`;
        return this.httpClient.delete(endpoint, {});
    }

    copyPartiallyApplicationByNotary(appId): Observable<any> {
      const endpoint = `${this.bff}/application/copynew/${appId}`;
      return this.httpClient.post(endpoint, {});
    }

    backApplicationByNotary(appId): Observable<any> {
      const endpoint = `${this.bff}/application/${appId}/notary/reactivate`;
      return this.httpClient.post(endpoint, {});
    }

    copyApplicationByNotary(appId): Observable<any> {
      const endpoint = `${this.bff}/application/copy/${appId}`;
      return this.httpClient.post(endpoint, {});
    }

    initiatePayment(applicationId: number, cadID: string): void {
        window.location.href = `${this.bff}/application/${applicationId}/payment/initiate?cadOfficeId=${cadID}`;
    }

    uploadApplicationContract(appId, fileToUpload: File): Observable<any> {
        const endpoint = `${this.bff}/document/notary-contract/application/${appId}`;
        const formData: FormData = new FormData();
        formData.append('file', fileToUpload, fileToUpload.name);
        return this.httpClient.post(endpoint, formData);
    }

    downloadFile(fileData, contentType, fileName) {
        const binaryData = [];
        binaryData.push(fileData);
        const downloadLink = document.createElement('a');
        downloadLink.href = window.URL.createObjectURL(
            new Blob(binaryData, { type: contentType })
        );
        downloadLink.setAttribute('download', fileName);
        document.body.appendChild(downloadLink);
        downloadLink.click();
    }

    showFileInNewTab(fileData) {
        const binaryData = [];
        binaryData.push(fileData);
        const downloadLink = document.createElement('a');
        downloadLink.href = window.URL.createObjectURL(
            new Blob(binaryData, { type: 'application/pdf' })
        );
        downloadLink.setAttribute('target', '_blank');
        document.body.appendChild(downloadLink);
        downloadLink.click();
    }

    onSuccessUpdateDocument(message: string, header?: string): void {
        const modalRef = this.modalService.open(DialogGenericComponent, {
            centered: true,
            size: 'md',
            backdropClass: 'dialog-backdrop',
            windowClass: 'dialog-background-blue',
            beforeDismiss: () => false,
        });
        const config: DialogConfig = {
            body: [
                {
                    label: `${message}`,
                    classes: 'text-center',
                },
            ],
            header: header,
            type: DIALOG_TYPE.GENERIC,
            actions: [
                {
                    label: 'Κλείσιμο',
                    actionClass: 'btn-secondary',
                    actionCallback: () => {},
                },
            ],
        };
        modalRef.componentInstance.config = config;
        modalRef.result.then((cb) => {
            cb();
        });
    }

    onFailUpdateDocument(errorMessage: string): void {
        const modalRef = this.modalService.open(DialogGenericComponent, {
            centered: true,
            size: 'md',
            backdropClass: 'dialog-backdrop',
            windowClass: 'dialog-background-blue',
            beforeDismiss: () => false,
        });
        const config: DialogConfig = {
            body: [
                {
                    label: errorMessage,
                    classes: 'text-center',
                },
            ],
            header: 'Ενημέρωση',
            type: DIALOG_TYPE.GENERIC,
            actions: [
                {
                    label: 'Συνέχεια',
                    actionClass: 'btn-secondary',
                    actionCallback: () => {},
                },
            ],
        };
        modalRef.componentInstance.config = config;
        modalRef.result.then((cb) => {
            cb();
        });
    }

    openGenericDialog(
        header: string,
        dialogText: string,
        actions: DialogAction[]
    ): void {
        const modalRef = this.modalService.open(DialogGenericComponent, {
            centered: true,
            size: 'md',
            backdropClass: 'dialog-backdrop',
            windowClass: 'dialog-background-blue',
            beforeDismiss: () => false,
        });

        const config: DialogConfig = {
            body: [{ label: dialogText, classes: 'text-center' }],
            header,
            type: DIALOG_TYPE.GENERIC,
            actions,
        };
        modalRef.componentInstance.config = config;
        modalRef.result.then((cb) => {
            cb();
        });
    }

    getPaymentDoc(appId: number, cadOfficeId: number) {
        return this.httpClient
            .get<any>(
                `${this.bff}/application/${appId}/payment/doc?cadOfficeId=${cadOfficeId}&applTypeId=${this.selectedApplicationType}`,
                { responseType: 'blob' as 'json', observe: 'response' }
            )
            .pipe(catchError(this.handleError))
            .subscribe({
                next: (response) => {
                    this.showFileInNewTab(response?.body);
                },
            });
    }

    getTemplateDoc(appId: number) {
      return this.httpClient
          .get<any>(
              `${this.bff}/application/${appId}/contract/download?applType=${this.selectedApplicationType}`,
              { responseType: 'blob' as 'json', observe: 'response' }
          )
          .pipe(catchError(this.handleError))
          .subscribe({
              next: (response) => {
                  const blob = new Blob([response.body], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
                  const url = window.URL.createObjectURL(blob);

                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `contract_template_${appId}.docx`;
                  a.click();

                  window.URL.revokeObjectURL(url);
              },
          });
    }

    getLegalStatusDoc(appId: number, cadOfficeId: number) {
        return this.httpClient
            .get<any>(
                `${this.bff}/application/${appId}/contract-info/legal${cadOfficeId}`,
                { responseType: 'blob' as 'json', observe: 'response' }
            )
            .pipe(catchError(this.handleError))
            .subscribe({
                next: (response) => {
                    this.showFileInNewTab(response?.body);
                },
            });
    }
    getTransferCertificationDoc(appId: number, cadOfficeId: number) {
        return this.httpClient
            .get<any>(
                `${this.bff}/application/${appId}/submission-doc?cadOfficeId=${cadOfficeId}`,
                { responseType: 'blob' as 'json', observe: 'response' }
            )
            .pipe(catchError(this.handleError))
            .subscribe({
                next: (response) => {
                    this.showFileInNewTab(response?.body);
                },
            });
    }
    getContractCharges(
        appId,
        contractValue,
        numOfReqCertificates,
        objectiveValue
    ) {
        let params = new HttpParams();

        params = params.append('contractValue', contractValue);
        params = params.append('numOfReqCertificates', numOfReqCertificates);
        params = params.append('objectiveValue', objectiveValue);

        return this.httpClient.get<any>(
            `${this.bff}/application/${appId}/contract-charges`,
            { params }
        );
    }

    private sellersFormInput: BehaviorSubject<InputFormStructure[]> =
        new BehaviorSubject<InputFormStructure[]>([]);
    sellersFormInput$ = this.sellersFormInput.asObservable();

    private sellersFormInputFinal: BehaviorSubject<InputFormStructure[]> =
        new BehaviorSubject<InputFormStructure[]>([]);
    sellersFormInputFinal$ = this.sellersFormInputFinal.asObservable();

    private buyersFormInput: BehaviorSubject<InputFormStructure[]> =
        new BehaviorSubject<InputFormStructure[]>([]);
    buyersFormInput$ = this.buyersFormInput.asObservable();

    private buyersFormInputFinal: BehaviorSubject<InputFormStructure[]> =
        new BehaviorSubject<InputFormStructure[]>([]);
    buyersFormInputFinal$ = this.buyersFormInputFinal.asObservable();

    private formInputWithoutName: BehaviorSubject<string> =
        new BehaviorSubject<string>(null);
    formInputWithoutName$ = this.formInputWithoutName.asObservable();

    private transactionStartSubject = new Subject<boolean>();

    private transactionStartResponseHelper$ = combineLatest([
        this.sellersFormInputFinal$,
        this.buyersFormInputFinal$,
        this.userInfo$,
    ]).pipe(
        map(([sellers, buyers, userInfo]) => ({
            // sellerList: sellers.map((seller) => {seller.value, console.log(seller);}),
            sellerList: sellers.map((seller) => {

              let ret = {
                "participantTaxNumber": seller.value,
                "representatives": [
                  {
                    "userTaxNumber": seller.representativeAfm,
                    "userEmail": seller.representativeEmail
                  }
                ]
              };

              return ret;
            }),
            buyerList: buyers.map((buyer) => {
              let ret = {
                "participantTaxNumber": buyer.value,
                "representatives": [
                  {
                    "userTaxNumber": buyer.representativeAfm,
                    "userEmail": buyer.representativeEmail
                  }
                ]
              };

              if(buyer.representativeAfm2 && buyer.representativeAfm2.length > 0)
              {
                ret['representatives'].push({
                  "userTaxNumber": buyer.representativeAfm2,
                  "userEmail": ''
                });
              }

              return ret;
            }),
            notaryAfm: userInfo.taxNumber,
            notaryRegId: userInfo.notaryRegId,
            notaryUniqueId: userInfo.notaryUniqueId,
        }))
    );


    transactionStartResponse$ = this.transactionStartSubject.pipe(
        withLatestFrom(this.transactionStartResponseHelper$),
        map(([subject, obs]) => obs),
        mergeMap((res) =>
              this.httpClient
                  .post(
                      `${this.bff}/application/notary`,
                      {
                          applicationType: this.selectedApplicationType,
                          sellers: res.sellerList,
                          buyers: res.buyerList,
                      },
                      { headers: new HttpHeaders().set('skipRetry', 'true') }
                  )
                  .pipe(
                      map((application_id) => [application_id]),
                      catchError(this.handleError)
                  )
          )
    );

    getUserRoleLabel(role: USER_ROLE, taxNumber, individual: boolean = true): string {
        const backOfficersTaxNumbers = utils.getBackOfficersTaxNumbers();

        const user = this.user.getValue();

        if (role === USER_ROLE.CITIZEN) {
            if (backOfficersTaxNumbers.includes(taxNumber))
                return 'Back Officer';
            else{
                if(individual == true)
                    return 'Πολίτης';
                else
                    return 'Νομικό Πρόσωπο';
            }
        } else if (role === USER_ROLE.NOTARY) return 'Συμβολαιογράφος';
        else return null;

        /*
        switch (role) {
            case USER_ROLE.CITIZEN:
                return 'Πολίτης';
            case USER_ROLE.NOTARY:
                return 'Συμβολαιογράφος';
            default:
                return null;
        }
        */
    }

    getUserCapacityLabel(capacity: USER_CAPACITY): string {
        switch (capacity) {
            case USER_CAPACITY.BUYER:
                return capitalizeFirstLetter(this.getTranferTypeTextKey('acquiringSingular1'));
            case USER_CAPACITY.SELLER:
                return capitalizeFirstLetter(this.getTranferTypeTextKey('transferringSingular1'));
            default:
                return null;
        }
    }
    httpOptions = {
        headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
    };

    downloadDocument(document): Observable<any> {
        return this.httpClient.get(
            `${this.bff}/document/${document.docId}/application/${document.appId}`,
            { responseType: 'json' }
        );
    }

    fetchContractId(document): Observable<any> {
        console.log(document);
        return this.httpClient.get(
            `${this.bff}/document/notary-contract/application/${document.appId}`,
            this.httpOptions
        );
    }

    // ---------------------------------------- NEW STATE PATTERN ---------------------------------------- //

    constructor(
        private httpClient: HttpClient,
        private router: Router,
        private modalService: NgbModal,
        private storage: LocalStorageService
    ) {}

    // ---------------------------------------- NEW SUBJECT CHANGES ---------------------------------------- //

    public onSelectUserRole(selectedRole: USER_ROLE) {
        const oldState = this.user.getValue();
        if (oldState.selectedRole === null) {
            this.user.next({ ...oldState, selectedRole });
        }
    }

    public setIndividual(individual: boolean) {
        const oldState = this.user.getValue();
        this.user.next({ ...oldState, individual });
    }

    public setUnderage(underage: boolean) {
        const oldState = this.user.getValue();
        this.user.next({ ...oldState, underage });
    }

    public onTermsAcceptanceChange(termsAcceptance: boolean) {
        const oldState = this.user.getValue();
        this.user.next({ ...oldState, termsAcceptance });
    }
    getContractInfo(appid: string) {
        return this.httpClient
            .get(`${this.bff}/application/${appid}/contract-info?applTypeId=${this.selectedApplicationType}`)
            .pipe(catchError(this.handleError));
    }
    getContractDocuments(appID: string, documentID) {
        const endpoint = `${this.bff}/application/${appID}/contract-info/${documentID}`;
        return this.httpClient
            .get(endpoint, {
                responseType: 'blob' as 'json',
                observe: 'response',
            })
            .pipe(catchError(this.handleError));
    }
    onSubmitBuyersOrSellersForm(
        afmInputs: InputFormValue[],
        flag: USER_CAPACITY
    ) {
        let formInput;
        let formInputFinal;
        let headers;
        switch (flag) {
            case USER_CAPACITY.BUYER:
                formInput = this.buyersFormInput;
                formInputFinal = this.buyersFormInputFinal;
                headers = new HttpHeaders({
                    reqId: 'name-initials-buyers',
                });
                break;
            case USER_CAPACITY.SELLER:
                formInput = this.sellersFormInput;
                formInputFinal = this.sellersFormInputFinal;
                headers = new HttpHeaders({
                    reqId: 'name-initials-sellers',
                });
                break;
            default:
                return;
        }
        let oldValues = formInput.getValue();
        const newValues: InputFormStructure[] = [];
        let errorMessage = '',
            errorMessageDeceased = '',
            errorMessageCompany = '',
            errorMessageUnderage = '',
            error = false;
        let params = new HttpParams();
        afmInputs.forEach((input) => {
            params = params.append('usersTaxNumbers', input.afmInput);
            params = params.append('applType', this.selectedApplicationType);
            params = params.append('involvedPartyType', flag);
        });
        this.httpClient
            .get(`${this.bff}/user/list-users/initials`, {
                headers,
                params,
            })
            .pipe(map((res) => utils.mapNameInitials(res)))
            .subscribe(
                (users) => {
                    let checkCompanyRepresentativeAfms = false,
                        checkUnderageRepresentativeAfms = false;
                    afmInputs.forEach((input) => {
                        const user = users.find(
                            (user) => user.taxNumber === input.afmInput
                        );

                        if (user && user.validAfm) {

                            //search and delete duplicates
                            if(oldValues.length > 0){
                                oldValues = oldValues.filter(item => item.value !== input.afmInput)
                            }

                            //check if company
                            let individual = true;
                            let submitted = true;
                            if(typeof user.individual !== 'undefined' && user.individual === false){

                              if(this.selectedApplicationType == TRANSER_TYPE.PARENTAL){
                                errorMessageCompany += input.afmInput + ', ';
                                error = true;
                              }else {
                                individual = false;
                                submitted = false;
                                checkCompanyRepresentativeAfms = true;
                              }

                            }

                            //check if underage
                            let underage = false;
                            if(typeof user.underage !== 'undefined' && user.underage === true){
                              if(this.selectedApplicationType == TRANSER_TYPE.PARENTAL && flag == USER_CAPACITY.BUYER){
                                underage = true;
                                submitted = false;
                                checkUnderageRepresentativeAfms = true;
                              }
                              else{
                                errorMessageUnderage += input.afmInput + ', ';
                                error = true;
                              }
                            }

                            if(!error){
                              newValues.push({
                                  required: true,
                                  submitted: submitted,
                                  type: 'afm-input',
                                  value: input.afmInput,
                                  representativeAfm: input.representativeAfm,
                                  representativeAfm2: input.representativeAfm2,
                                  representativeError: 0,
                                  representativeEmail: input.representativeEmail,
                                  fullName: user.nameInitials,
                                  error: !user.emailFound,
                                  noValidAfm: !user.validAfm,
                                  individual: individual,
                                  underage: underage,
                              });
                            }
                        } else {
                            if(this.selectedApplicationType == TRANSER_TYPE.INHERITANCE && flag == USER_CAPACITY.SELLER)
                              errorMessageDeceased += input.afmInput + ', ';
                            else
                              errorMessage += input.afmInput + ', ';
                        }
                    });

                    const values = [...oldValues, ...newValues];
                    formInput.next(values);

                    const finalValues = values.filter(user => user.submitted === true);
                    formInputFinal.next(finalValues);

                    if (errorMessage !== '' || errorMessageDeceased !== '' || errorMessageCompany !== '' || errorMessageUnderage !== '') {
                      if (errorMessage !== '')
                        errorMessage = `ΑΦΜ που δεν αντιστοιχούν σε ονοματεπώνυμο: ${errorMessage.slice(0, -2)}.`;

                      if (errorMessageDeceased !== '')
                        errorMessage += `ΑΦΜ που δεν αντιστοιχούν σε θανόντα: ${errorMessageDeceased.slice(0, -2)}.`;

                      if (errorMessageCompany !== '')
                        errorMessage += `ΑΦΜ που αντιστοιχεί σε εταιρία: ${errorMessageCompany.slice(0, -2)}.`;

                      if (errorMessageUnderage !== '')
                        errorMessage += `ΑΦΜ που αντιστοιχεί σε ανήλικο: ${errorMessageUnderage.slice(0, -2)}.`;

                      this.formInputWithoutName.next(errorMessage);

                    } else {
                        this.formInputWithoutName.next(null);
                    }


                    //check representatives if needed
                    if(checkCompanyRepresentativeAfms && errorMessage === '')
                        this.checkCompanyRepresentativeContactDetails(values, headers, formInput, formInputFinal, flag);
                    else if(checkUnderageRepresentativeAfms && errorMessage === '')
                        this.checkUnderageRepresentativeContactDetails(values, headers, formInput, formInputFinal, flag);

                },
                (err) => console.log(err)
            );
    }

    checkCompanyRepresentativeContactDetails(values: InputFormStructure[], headers: HttpHeaders, formInput: BehaviorSubject<InputFormStructure[]>, formInputFinal: BehaviorSubject<InputFormStructure[]>, flag: USER_CAPACITY): void{
        let errorMessage = '';
        let shouldRequest = false;
        let params = new HttpParams();
        params = params.append('applType', this.selectedApplicationType);
        params = params.append('involvedPartyType', flag);
        values.filter(user => user.submitted === false && user.individual === false).forEach((input) => {
            if(input.representativeAfm.length == 9 && this.validateEmail(input.representativeEmail)){
                params = params.append('usersTaxNumbers', input.representativeAfm);
                shouldRequest = true;
            }
            else{
                if(input.representativeAfm.length == 0)
                    errorMessage += '';
                else if(input.representativeAfm.length != 9)
                    errorMessage += 'Το ΑΦΜ αντιπροσώπου δεν είναι σωστό.';
                else
                    errorMessage += 'Το Email αντιπροσώπου δεν είναι σωστό.';
            }
        });

        if(shouldRequest){
            this.httpClient
            .get(`${this.bff}/user/list-representatives/initials`, {
                headers,
                params,
            })
            .pipe(map((res) => utils.mapNameInitials(res)))
            .subscribe(
                (users) => {
                    let em = '';
                    users.forEach((input) => {

                        if (input && input.validAfm && input.individual) {
                            for (let i = 0; i < values.length; i++){
                                if(values[i].representativeAfm == input.taxNumber)
                                {
                                    values[i].submitted = true;
                                    values[i].error = !input.emailFound;
                                }
                            }
                        } else {
                            em += input.taxNumber + ', ';
                        }
                    });

                    //if there are afms that were not found
                    if(em != ''){
                        em = `ΑΦΜ εκπροσώπων που δεν αντιστοιχούν σε ονοματεπώνυμο: ${em.slice(0, -2)}.`;
                        this.formInputWithoutName.next(em);
                    }

                    formInput.next([...values]);

                    const finalValues = values.filter(user => user.submitted === true);
                    formInputFinal.next(finalValues);
                },
                (err) => console.log(err)
            );
        }

        //show error
        if (errorMessage !== '') {
            this.formInputWithoutName.next(errorMessage);
        } else {
            this.formInputWithoutName.next(null);
        }
    }

    checkUnderageRepresentativeContactDetails(values: InputFormStructure[], headers: HttpHeaders, formInput: BehaviorSubject<InputFormStructure[]>, formInputFinal: BehaviorSubject<InputFormStructure[]>, flag: USER_CAPACITY): void{
        let errorMessage = '';
        let representativeFields = ['representativeAfm', 'representativeAfm2'];
        let shouldSubmit = [];
        let emailFound = [];
        let numberOfAfmsGiven = 0;
        let tmpInput = {taxNumber: '', emailFound: false};
        for (let i = 0; i < representativeFields.length; i++) {
          let shouldRequest = false;
          let params = new HttpParams();
          params = params.append('applType', this.selectedApplicationType);
          params = params.append('involvedPartyType', flag);
          values.filter(user => user.submitted === false && user.underage === true).forEach((input) => {
            if(input[representativeFields[i]].length == 0)
              return;

            if(i == 0 && input[representativeFields[0]] == input[representativeFields[1]])
              errorMessage += `Τα ΑΦΜ αντιπροσώπων δεν μπορούν να είναι τα ίδια.`;

            if(input[representativeFields[i]].length != 9)
              errorMessage += `Το ΑΦΜ αντιπροσώπου ${i + 1} δεν είναι σωστό.`;
            else
              params = params.append('usersTaxNumbers', input[representativeFields[i]]);

            if(errorMessage == ''){
              shouldRequest = true;
              numberOfAfmsGiven++;
            }
          });

          if(shouldRequest){
              this.httpClient
              .get(`${this.bff}/user/list-representatives/initials`, {
                  headers,
                  params,
              })
              .pipe(map((res) => utils.mapNameInitials(res)))
              .subscribe(
                  (users) => {
                      let em = '';
                      users.forEach((input) => {
                        let submitted = false;
                        if (input && input.validAfm && input.individual && !input.underage)
                          submitted = true;
                        else
                            em += input.taxNumber + ', ';

                        shouldSubmit.push(submitted);
                        emailFound.push(input.emailFound);
                        tmpInput = input;
                      });

                      //if there are afms that were not found
                      if(em != ''){
                        em = `ΑΦΜ εκπροσώπων που δεν αντιστοιχούν σε ονοματεπώνυμο ενήλικα: ${em.slice(0, -2)}.`;
                        this.formInputWithoutName.next(em);
                      }

                      if(shouldSubmit.length == numberOfAfmsGiven && numberOfAfmsGiven != 0){
                        let submit = true;
                        let allEmailsFound = true;
                        let representativeError = 0;
                        for (let i = 0; i < shouldSubmit.length; i++){
                          submit = shouldSubmit[i] && submit;
                          allEmailsFound = allEmailsFound && emailFound[i];
                          if(!emailFound[i])
                            representativeError = i + 1;
                        }

                        if(submit){
                          values.filter(user => user.submitted === false && user.underage === true).forEach((input) => {
                            for (let i = 0; i < values.length; i++){
                                if(values[i]['representativeAfm'] == tmpInput.taxNumber || values[i]['representativeAfm2'] == tmpInput.taxNumber)
                                {
                                    values[i].submitted = true;
                                    values[i].error = !allEmailsFound;
                                    values[i].representativeError = representativeError;
                                }
                              }
                            });

                          formInput.next([...values]);

                          const finalValues = values.filter(user => user.submitted === true);
                          formInputFinal.next(finalValues);
                        }
                      }

                  },
                  (err) => console.log(err)
              );
          }

          //show error
          if (errorMessage !== '') {
              this.formInputWithoutName.next(errorMessage);
              break;
          } else {
              this.formInputWithoutName.next(null);
          }
        }
    }


    onFailFormFieldsValidation(errorMessage: string): void {
        const modalRef = this.modalService.open(DialogGenericComponent, {
            centered: true,
            size: 'md',
            backdropClass: 'dialog-backdrop',
            windowClass: 'dialog-background-blue',
            beforeDismiss: () => false,
        });
        const config: DialogConfig = {
            body: [
                {
                    label: errorMessage,
                    classes: 'text-left',
                },
            ],
            header: 'Ενημέρωση',
            type: DIALOG_TYPE.GENERIC,
            actions: [
                {
                    label: 'Κλείσιμο',
                    actionClass: 'btn-secondary',
                    actionCallback: () => {},
                },
            ],
        };
        modalRef.componentInstance.config = config;
        modalRef.result.then((cb) => {
            cb();
        });
    }

    getNotaryContactDetails(afmInputs: InputFormValue[], flag: USER_CAPACITY) {
        let headers;
        switch (flag) {
            case USER_CAPACITY.NOTARY:
                headers: new HttpHeaders({
                    reqId: 'name-initials-notary',
                    customId: 'getNotaryContactDetails',
                }).set('skipLoader', 'true');
                break;
            default:
                return;
        }
        let params = new HttpParams();
        afmInputs.forEach((input) => {
            params = params.append('usersTaxNumbers', input.afmInput);
        });
        return this.httpClient
            .get(`${this.bff}/user/list-users/initials`, {
                headers,
                params,
            })
            .pipe(map((res) => utils.mapNameInitials(res)));
    }

    public onRemoveBuyerPill = (afm: string) => {
        const oldValues = this.buyersFormInputFinal.getValue();
        const filteredValues = oldValues.filter((item) => item.value !== afm);
        this.buyersFormInput.next(filteredValues);
        this.buyersFormInputFinal.next(filteredValues);
    };

    public onRemoveSellerPill = (afm: string) => {
        const oldValues = this.sellersFormInputFinal.getValue();
        const filteredValues = oldValues.filter((item) => item.value !== afm);
        this.sellersFormInput.next(filteredValues);
        this.sellersFormInputFinal.next(filteredValues);
    };

    public onTransactionStart(): void {
        this.transactionStartSubject.next(true);
    }

    public clearNotaryTransactionStart(): void {
        this.sellersFormInput.next([]);
        this.buyersFormInput.next([]);
        this.formInputWithoutName.next(null);
    }

    public updateAKD(appId: string, docId: string, fields: string[]) {
        let errorMessage = null;
        if (fields === null || fields.length !== 2) {
            errorMessage = 'Πρέπει να συπληρωθούν όλα τα πεδία για το Α.Κ.Δ.';
        }
        fields.forEach((item) => {
            if (item === null || item === '') {
                errorMessage =
                    'Πρέπει να συπληρωθούν όλα τα πεδία για το Α.Κ.Δ.';
            }
        });
        if (errorMessage) {
            return throwError({
                type: 'CLIENT_SIDE',
                error: { message: errorMessage },
            });
        }

        const payload = {
            protNum: +fields[0],
            applyYear: +fields[1],
            // applicationOffice: fields[2],
            // applicationId: appId,
            // documentId: docId,
            // afm: null,
            // propHor: 1,
            // propVer: 1,
        };
        // this.userInfo$
        //     .pipe(take(1))
        //     .subscribe((value) => (payload.afm = value.taxNumber));
        console.log(payload);

        return this.httpClient.put(
            `${this.bff}/document/${docId}/input-parameters`,
            payload
        );
    }

    public updateProofOfPayment(
        docId: number,
        transId: number,
        arDilwsis: number,
        doyArDilwsis: number,
        doyXrisis: number
    ) {
        console.log(
            transId + ' ' + arDilwsis + ' ' + doyArDilwsis + ' ' + doyXrisis
        );
        const payload = {
            transId: Number(transId),
            arDilwsis: Number(arDilwsis),
            doyArDilwsis: Number(doyArDilwsis),
            doyXrisis: Number(doyXrisis),
        };
        return this.httpClient.put(
            `${this.bff}/document/${docId}/input-parameters`,
            payload
        );
    }

    public updateKHD(docId: string, inputParams: any) {
        const payload = { diagramId: +inputParams[1] };
        return this.httpClient.put(
            `${this.bff}/document/${docId}/input-parameters`,
            payload
        );
    }

    onApplicationAccept(appId) {
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        'applTypeId': this.selectedApplicationType
      });

      return this.httpClient
          .put(`${this.bff}/application/${appId}/citizen/accept`, {}, {headers: headers})
          .pipe(catchError(this.handleError));
    }

    onApplicationDecline(appId) {
        return this.httpClient
            .put(`${this.bff}/application/${appId}/citizen/decline`, {})
            .pipe(catchError(this.handleError));
    }

    onPropertySelect(appId, body) {
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
        });
        this.userInfo$.pipe(take(1)).subscribe((userInfo) => {
            headers.append('taxNumber', userInfo.taxNumber);

        });
        return this.httpClient
          .post(`${this.bff}/application/${appId}/citizen/property?applTypeId=${this.selectedApplicationType}`, body, {
              headers: headers,
          })
          .pipe(catchError(this.handleErrorPerStatus));
    }

    getPaymentDetailsResponse(orderCode): Observable<any> {
        return this.httpClient
            .get(`${this.bff}/application/payment/details/${orderCode}`)
            .pipe(catchError(this.handleError));
    }

    onPropertyBuy(appId, atakList: string[]) {
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        'applTypeId': this.selectedApplicationType
      });

      return this.httpClient
          .post(
              `${this.bff}/application/${appId}/citizen/propertybuy`,
              atakList,
              {
                headers: headers,
              }
          )
          .pipe(catchError(this.handleError));
    }

    onDocumentStatusChange(docId, value) {
        const headers = new HttpHeaders({
            reqId: 'refresh',
        });
        const params = new HttpParams().set('value', value);
        return this.httpClient
            .put(
                `${this.bff}/document/${docId}/notary/set-status`,
                {},
                { headers, params }
            )
            .pipe(catchError(this.handleError));
    }
    public sendDataToCadastre(appid, body) {
        return this.httpClient
            .put(
                `${this.bff}/application/${appid}/notary/submit/cadastre`,
                body,
                {
                    headers: new HttpHeaders().set('skipRetry', 'true'),
                }
            )
            .pipe(catchError(this.handleErrorCadastreSubmission));
    }
    public onRefreshNotaryApplicationWithId() {
        this.refreshNotaryApplicationWithId.next(true);
    }

    public onRefreshNotaryApplicationDocuments() {
        this.refreshNotaryApplicationDocuments.next(true);
        this.refreshNotaryApplicationDocumentsStatus.next(true);
        //this.refreshNotaryApplicationWithId.next(true);
    }

    public refreshDocStatus() {
        this.refreshNotaryApplicationDocuments.next(true);
    }

    public getDocumentTypeNameFromID(id: string): string {
        let documentTypeName = '';
        switch (id) {
            case '1':
                documentTypeName = 'ΤΕΧΝΙΚΑ - ΑΠΟΣΠΑΣΜΑ ΚΤΗΜ.ΔΙΑΓΡΑΜΜΑΤΟΣ';
                break;
            case '8':
                documentTypeName = 'ΤΕΧΝΙΚΑ - ΚΤΗΜΑΤΟΓΡΑΦΙΚΟ ΔΙΑΓΡΑΜΜΑ';
                break;
            case '9':
                documentTypeName = 'ΤΕΧΝΙΚΑ - ΔΙΑΓΡΑΜΜΑ  ΓΕΩΜΕΤΡΙΚΩΝ ΜΕΤΑΒΟΛΩΝ';
                break;
            case '10':
                documentTypeName = 'ΤΕΧΝΙΚΑ - ΤΟΠΟΓΡΑΦΙΚΟ ΔΙΑΓΡΑΜΜΑ';
                break;
            case '12':
                documentTypeName = 'ΑΙΤΗΣΗ ΚΑΤΑΧΩΡΙΣΗΣ';
                break;
            case '13':
                documentTypeName = 'ΠΕΡΙΛΗΨΗ ΕΓΓΡΑΠΤΕΑΣ ΠΡΑΞΗΣ';
                break;
            case '15':
                documentTypeName = 'ΑΠΟΤΕΛΕΣΜΑ ΕΛΕΓΧΟΥ';
                break;
            case '16':
                documentTypeName = 'ΤΕΧΝΙΚΗ ΕΙΣΗΓΗΣΗ';
                break;
            case '17':
                documentTypeName = 'ΣΤΟΙΧΕΙΑ ΕΠΙΚΟΙΝΩΝΙΑΣ ΥΠΟΒΑΛΛΟΝΤΟΣ';
                break;
            case '18':
                documentTypeName = 'ΦΟΡΟΛΟΓΙΚΑ';
                break;
            case '19':
                documentTypeName = 'ΝΟΜΙΜΟΠΟΙΗΤΙΚΑ/ΛΟΙΠΑ';
                break;
            case '20':
                documentTypeName = 'ΑΠΟΦΑΣΗ ΠΡΟΪΣΤΑΜΕΝΟΥ ΓΙΑ ΧΜ';
                break;
            case '21':
                documentTypeName = 'ΕΚΘΕΣΗ ΕΠΙΔΟΣΗΣ';
                break;
            case '18':
                documentTypeName = 'ΦΟΡΟΛΟΓΙΚΑ';
                break;
            default:
                documentTypeName = 'Unknown document Id';
                break;
        }
        return documentTypeName;
    }
    getLegalStatus(statusId: string) {
        let data = '';
        switch (statusId) {
            case '0':
                data = 'Απόρριψη';
                break;
            case '1':
                data = 'Αποδοχή';
                break;
            case '2':
                data = 'Μερική Αποδοχή';
                break;
            default:
                data = '';
                break;
        }
        return data;
    }
    saveManuallyCadastreForm(appId: string, body) {
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
        });
        this.userInfo$.pipe(take(1)).subscribe((userInfo) => {
            headers.append('taxNumber', userInfo.taxNumber);
        });

        return this.httpClient
            .post(`${this.bff}/application/${appId}/int-notary`, body, {
                headers: headers,
            })
            .pipe(catchError(this.handleErrorPerStatus));
    }

    getPaymentMethod(method) {
        if (method == 'RF_PAYMENT') {
            return 'Κωδικό Πληρωμής RF';
        }
        if (method == 'CREDIT_CARD') {
            return 'Κάρτα';
        }
        return 'No payment Method';
    }






    //transfer types
    transferTypes = {
      "": ' - ',
      "1": 'ΑΓΟΡΑΠΩΛΗΣΙΑ',
      "2": 'ΔΩΡΕΑ',
      "3": 'ΓΟΝΙΚΗ ΠΑΡΟΧΗ',
      "4": 'ΚΛΗΡΟΝΟΜΙΑ',
    }

    transferTypeTexts = {
      "1": {
        typeSingular1: 'αγοραπωλησία',
        typeSingular2: 'αγοραπωλησίας',
        transferringSingular1: 'πωλητής',
        transferringSingular2: 'πωλητή',
        transferringSingular3: 'πωλητή',
        transferringPlural1: 'πωλητές',
        transferringPlural2: 'πωλητών',
        transferringPlural3: 'πωλητές',
        acquiringSingular1: 'αγοραστής',
        acquiringSingular2: 'αγοραστή',
        acquiringSingular3: 'αγοραστή',
        acquiringPlural1: 'αγοραστές',
        acquiringPlural2: 'αγοραστών',
        acquiringPlural3: 'αγοραστές',
      },
      "2": {
        typeSingular1: 'δωρεά',
        typeSingular2: 'δωρεάς',
        transferringSingular1: 'δωρητής',
        transferringSingular2: 'δωρητή',
        transferringSingular3: 'δωρητή',
        transferringPlural1: 'δωρητές',
        transferringPlural2: 'δωρητών',
        transferringPlural3: 'δωρητές',
        acquiringSingular1: 'δωρεοδόχος',
        acquiringSingular2: 'δωρεοδόχου',
        acquiringSingular3: 'δωρεοδόχο',
        acquiringPlural1: 'δωρεοδόχοι',
        acquiringPlural2: 'δωρεοδόχων',
        acquiringPlural3: 'δωρεοδόχους',
      },
      "3": {
        typeSingular1: 'γονική παροχή',
        typeSingular2: 'γονικής παροχής',
        transferringSingular1: 'παρέχων',
        transferringSingular2: 'παρέχοντος',
        transferringSingular3: 'παρέχοντα',
        transferringPlural1: 'παρέχοντες',
        transferringPlural2: 'παρεχόντων',
        transferringPlural3: 'παρέχοντες',
        acquiringSingular1: 'δεχόμενος',
        acquiringSingular2: 'δεχόμενου',
        acquiringSingular3: 'δεχόμενο',
        acquiringPlural1: 'δεχόμενοι',
        acquiringPlural2: 'δεχόμενων',
        acquiringPlural3: 'δεχόμενους',
      },
      "4": {
        typeSingular1: 'κληρονομιά',
        typeSingular2: 'κληρονομιάς',
        transferringSingular1: 'κληρονομούμενος',
        transferringSingular2: 'κληρονομούμενου',
        transferringSingular3: 'κληρονομούμενο',
        transferringPlural1: 'κληρονομούμενοι',
        transferringPlural2: 'Κληρονομούμενων',
        transferringPlural3: 'Κληρονομούμενους',
        acquiringSingular1: 'κληρονόμος',
        acquiringSingular2: 'κληρονόμου',
        acquiringSingular3: 'κληρονόμο',
        acquiringPlural1: 'κληρονόμοι',
        acquiringPlural2: 'κληρονόμων',
        acquiringPlural3: 'κληρονόμους',
      },
    }


    selectedApplicationType: string = "";


    public onTransferTypeChange(newTransferType: string) {
      this.selectedApplicationType = newTransferType;
      this.storage.setTransferType(newTransferType);
    }

    public formatStringWithTypeTexts(string: string, tmpTransferType: string = ""): string{
      const regex = /{{(.*?)}}/g;
      const matches = string.match(regex);
      if(matches){
        const results = matches.map(match => match.slice(2, -2).trim());
        for (let i = 0; i < results.length; i++)
          string = string.replace(`{{${results[i]}}}`, this.getTranferTypeTextKey(results[i], tmpTransferType));
      }
      return string;
    }

    public getTranferTypeTextKey(key, tmpTransferType = ""){
      if(this.selectedApplicationType == "")
        this.selectedApplicationType = this.storage.getTransferType();

      let transferType = "";
      if(tmpTransferType != "")
        transferType = tmpTransferType;
      else
        transferType = this.selectedApplicationType;

      if(this.transferTypeTexts[transferType][key])
        return this.transferTypeTexts[transferType][key];

      return '';
    }


    // ---------------------------------------- NEW SUBJECT CHANGES ---------------------------------------- //

    private handleError(err: any): Observable<never> {
        let errorMessage: string;
        // if (err.error instanceof ErrorEvent) {
        //     errorMessage = `An error occurred: ${err.error.message}`;
        // } else {
        //     errorMessage = `Backend returned code ${err.status}: ${err.body.error}`;
        // }
        console.error(err);
        errorMessage = 'Συνέβη κάποιο σφάλμα.';
        return throwError(errorMessage);
    }
    private handleErrorCadastreSubmission(err: any): Observable<never> {
      let errorMessage: string;
      console.error(err);
      /*
      if (err.error instanceof ErrorEvent) {
        errorMessage = `${err.error.message}`;
      } else {
        errorMessage = `${err.body.error}`;
      }
      */

      errorMessage = '';

      if (err.error) {
        if (err.error.message) {
          errorMessage = err.error.message;
        }
      }
      else if (err.body) {
        if (err.body.error) {
          errorMessage = err.body.error;
        }
      }

      if (errorMessage === '')
        errorMessage = 'Συνέβη κάποιο σφάλμα.';

      return throwError(errorMessage);
    }
    private handleErrorPerStatus(err: any): Observable<never> {
        let errorMessage: string;
        console.error(err);

        if (err.status === 409) {
            errorMessage = 'Υπάρχει άλλη αίτηση μεταβίβασης σε εξέλιξη για το παρόν ακίνητο.';
        } else {
            errorMessage = 'Συνέβη κάποιο σφάλμα.';
        }

        return throwError(errorMessage);
    }

    private validateEmail = (email) => {
      return String(email)
        .toLowerCase()
        .match(
          /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
        );
    };
}
